import React, { useState } from 'react';
import {
    Plus, Trash2
} from 'lucide-react';

// Refactored Components
import { RBDHeader } from '../components/analytics/RBDHeader';
import { RBDSidebar } from '../components/analytics/RBDSidebar';

interface RBDBlock {
    id: string;
    name: string;
    reliability: number;
    description?: string;
}

interface RBDPath {
    id: string;
    name: string;
    type: 'Series' | 'Parallel' | 'K-out-of-N';
    kValue?: number;
    blocks: RBDBlock[];
}

const RBDTool: React.FC = () => {
    const [paths, setPaths] = useState<RBDPath[]>([
        {
            id: 'path-1',
            name: 'System Assembly',
            type: 'Series',
            blocks: [
                { id: 'b1', name: 'Power Supply', reliability: 0.995 },
                { id: 'b2', name: 'Control Board', reliability: 0.985 },
                { id: 'b3', name: 'Motor Assembly', reliability: 0.970 }
            ]
        }
    ]);

    const [systemReliability, setSystemReliability] = useState<number | null>(null);

    const addPath = (type: 'Series' | 'Parallel' | 'K-out-of-N') => {
        const newPath: RBDPath = {
            id: `path-${Date.now()}`,
            name: `${type} Subsystem`,
            type,
            kValue: type === 'K-out-of-N' ? 2 : undefined,
            blocks: [
                { id: `b-${Date.now()}-1`, name: 'Component A', reliability: 0.95 },
                { id: `b-${Date.now()}-2`, name: 'Component B', reliability: 0.95 }
            ]
        };
        setPaths([...paths, newPath]);
    };

    const addBlock = (pathId: string) => {
        setPaths(paths.map(p => {
            if (p.id === pathId) {
                return {
                    ...p,
                    blocks: [...p.blocks, { id: `b-${Date.now()}`, name: 'New Component', reliability: 0.99 }]
                };
            }
            return p;
        }));
    };

    const removeBlock = (pathId: string, blockId: string) => {
        setPaths(paths.map(p => {
            if (p.id === pathId) {
                return { ...p, blocks: p.blocks.filter(b => b.id !== blockId) };
            }
            return p;
        }));
    };

    const removePath = (pathId: string) => {
        if (paths.length > 1) {
            setPaths(paths.filter(p => p.id !== pathId));
        }
    };

    const updateBlock = (pathId: string, blockId: string, updates: Partial<RBDBlock>) => {
        setPaths(paths.map(p => {
            if (p.id === pathId) {
                return {
                    ...p,
                    blocks: p.blocks.map(b => b.id === blockId ? { ...b, ...updates } : b)
                };
            }
            return p;
        }));
    };

    const calculateReliability = () => {
        let totalR = 1.0;

        paths.forEach(path => {
            let pathR = 0;

            if (path.type === 'Series') {
                pathR = path.blocks.reduce((acc, b) => acc * b.reliability, 1.0);
            } else if (path.type === 'Parallel') {
                pathR = 1 - path.blocks.reduce((acc, b) => acc * (1 - b.reliability), 1.0);
            } else if (path.type === 'K-out-of-N') {
                const avgR = path.blocks.reduce((acc, b) => acc + b.reliability, 0) / path.blocks.length;
                const n = path.blocks.length;
                const k = path.kValue || 1;

                const combinations = (nVal: number, kVal: number): number => {
                    if (kVal < 0 || kVal > nVal) return 0;
                    if (kVal === 0 || kVal === nVal) return 1;
                    if (kVal > nVal / 2) kVal = nVal - kVal;
                    let res = 1;
                    for (let i = 1; i <= kVal; i++) res = res * (nVal - i + 1) / i;
                    return res;
                };

                let prob = 0;
                for (let i = k; i <= n; i++) {
                    prob += combinations(n, i) * Math.pow(avgR, i) * Math.pow(1 - avgR, n - i);
                }
                pathR = prob;
            }

            totalR *= pathR;
        });

        setSystemReliability(totalR);
    };

    return (
        <div className="p-8 max-w-7xl mx-auto">
            <RBDHeader onCalculate={calculateReliability} />

            <div className="grid lg:grid-cols-4 gap-8">
                <RBDSidebar addPath={addPath} systemReliability={systemReliability} />

                <div className="lg:col-span-3 space-y-6">
                    {paths.map(path => (
                        <div key={path.id} className="bg-white rounded-xl border border-gray-200 overflow-visible shadow-sm relative group">
                            <div className="bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <input
                                        value={path.name}
                                        onChange={(e) => setPaths(paths.map(p => p.id === path.id ? { ...p, name: e.target.value } : p))}
                                        className="bg-transparent border-none font-bold text-gray-900 focus:ring-0 p-0"
                                    />
                                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded ${path.type === 'Series' ? 'bg-blue-100 text-blue-700' :
                                        path.type === 'Parallel' ? 'bg-green-100 text-green-700' : 'bg-purple-100 text-purple-700'
                                        }`}>
                                        {path.type}
                                    </span>
                                </div>
                                <div className="flex items-center gap-3">
                                    {path.type === 'K-out-of-N' && (
                                        <div className="flex items-center gap-2 text-sm text-gray-600">
                                            <span>Min (k):</span>
                                            <input
                                                type="number"
                                                value={path.kValue || 1}
                                                onChange={(e) => setPaths(paths.map(p => p.id === path.id ? { ...p, kValue: parseInt(e.target.value) } : p))}
                                                className="w-12 h-6 border-gray-300 rounded text-center text-xs"
                                            />
                                        </div>
                                    )}
                                    <button
                                        onClick={() => removePath(path.id)}
                                        className="text-gray-400 hover:text-red-500 transition-colors"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            <div className="p-8">
                                <div className={`flex flex-wrap items-center gap-6 justify-center ${path.type === 'Parallel' ? 'flex-col' : ''}`}>
                                    <div className="w-8 h-0.5 bg-gray-300 relative">
                                        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                                    </div>

                                    <div className={`flex flex-wrap gap-4 items-center justify-center ${path.type === 'Parallel' ? 'flex-col' : ''}`}>
                                        {path.blocks.map(block => (
                                            <div key={block.id} className="w-48 bg-white border-2 border-slate-200 rounded-lg p-3 hover:border-blue-400 transition-all shadow-sm">
                                                <div className="flex justify-between items-start mb-2">
                                                    <input
                                                        value={block.name}
                                                        onChange={(e) => updateBlock(path.id, block.id, { name: e.target.value })}
                                                        className="text-xs font-bold border-none bg-transparent p-0 w-32 focus:ring-0"
                                                    />
                                                    <button
                                                        onClick={() => removeBlock(path.id, block.id)}
                                                        className="text-gray-300 hover:text-red-500 transition-colors"
                                                    >
                                                        <Trash2 className="w-3 h-3" />
                                                    </button>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[10px] text-gray-500 font-bold">R:</span>
                                                    <input
                                                        type="number"
                                                        step="0.0001"
                                                        value={block.reliability}
                                                        onChange={(e) => updateBlock(path.id, block.id, { reliability: parseFloat(e.target.value) })}
                                                        className="flex-1 bg-gray-50 border border-gray-200 rounded px-2 py-1 text-xs text-blue-700 font-mono"
                                                    />
                                                </div>
                                            </div>
                                        ))}

                                        <button
                                            onClick={() => addBlock(path.id)}
                                            className="w-10 h-10 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 hover:bg-blue-50 transition-all"
                                        >
                                            <Plus className="w-5 h-5" />
                                        </button>
                                    </div>

                                    <div className="w-8 h-0.5 bg-gray-300 relative">
                                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-gray-300 rounded-full"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    <div className="flex justify-center py-4">
                        <div className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-full text-xs text-gray-400 font-medium">
                            Total Blocks: {paths.reduce((acc, p) => acc + p.blocks.length, 0)} | System Input → Output Linkage Valid
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RBDTool;
